import { escapeHtml } from "@/lib/tool-kit/poetry/acrostic";
import {
  type LineInfo,
  type Lookup,
  lineSyllables,
  lineWords,
  reachableTotals,
} from "@/lib/tool-kit/poetry/pronounce";
import { endWord, relate, type SlantKind } from "@/lib/tool-kit/poetry/rhyme";

/**
 * Fixed poetry forms as data: how many lines, how many syllables (or words) in each,
 * which lines rhyme, and which are refrains. `evaluateForm` checks a draft against one,
 * line by line, as the writer types. Honest templates, no AI.
 *
 * The rules follow the Academy of American Poets' glossary (poets.org) and, for the
 * cinquain, Adelaide Crapsey's own poems in *Verse* (1915), whose lines count 2-4-6-8-2.
 * Where a form is looser in practice than its definition (haiku in English, the sestet of
 * a Petrarchan sonnet) the template follows the common convention and says so.
 */

export type FormId =
  | "haiku"
  | "tanka"
  | "cinquain"
  | "diamante"
  | "limerick"
  | "shakespearean"
  | "petrarchan"
  | "villanelle";

export const FORM_IDS: readonly FormId[] = [
  "haiku",
  "tanka",
  "cinquain",
  "diamante",
  "limerick",
  "shakespearean",
  "petrarchan",
  "villanelle",
];

export interface FormLine {
  /** Allowed syllables, inclusive; null when the form does not count them. */
  syllables: readonly [number, number] | null;
  /** Required word count (the diamante), else null. */
  words: number | null;
  /** The rhyme group's letter, or null. */
  rhyme: string | null;
  /** The index of an earlier line this one repeats. */
  refrain: number | null;
  /** A key into the widget's guidance strings. */
  hint: string | null;
}

export interface FormSpec {
  id: FormId;
  lines: FormLine[];
  /** Stanza breaks come after these line indexes. */
  breaks: number[];
}

function line(partial: Partial<FormLine>): FormLine {
  return {
    syllables: null,
    words: null,
    rhyme: null,
    refrain: null,
    hint: null,
    ...partial,
  };
}

function beats(min: number, max = min): FormLine {
  return line({ syllables: [min, max] });
}

function rhymed(letters: string, range: readonly [number, number]): FormLine[] {
  return [...letters].map((rhyme) => line({ syllables: range, rhyme }));
}

const PENTAMETER: readonly [number, number] = [10, 11];

function sonnet(
  id: "shakespearean" | "petrarchan",
  letters: string,
  breaks: number[],
  hints: Record<number, string>
): FormSpec {
  const lines = rhymed(letters, PENTAMETER).map((entry, i) =>
    hints[i] ? { ...entry, hint: hints[i] } : entry
  );
  return { id, lines, breaks };
}

const VILLANELLE_HINTS: Record<number, string> = {
  0: "refrainOne",
  2: "refrainTwo",
};

function villanelle(): FormSpec {
  // A1 b A2 / a b A1 / a b A2 / a b A1 / a b A2 / a b A1 A2
  const rhymes = "ABAABAABAABAABAABAA";
  const refrains: Record<number, number> = {
    5: 0,
    8: 2,
    11: 0,
    14: 2,
    17: 0,
    18: 2,
  };
  const lines = [...rhymes].map((rhyme, i) =>
    line({
      rhyme,
      refrain: refrains[i] ?? null,
      hint: VILLANELLE_HINTS[i] ?? null,
    })
  );
  return { id: "villanelle", lines, breaks: [2, 5, 8, 11, 14] };
}

export const FORMS: Readonly<Record<FormId, FormSpec>> = {
  haiku: {
    id: "haiku",
    lines: [beats(5), beats(7), beats(5)],
    breaks: [],
  },
  tanka: {
    id: "tanka",
    lines: [
      beats(5),
      beats(7),
      line({ syllables: [5, 5], hint: "pivot" }),
      beats(7),
      beats(7),
    ],
    breaks: [],
  },
  cinquain: {
    id: "cinquain",
    lines: [beats(2), beats(4), beats(6), beats(8), beats(2)],
    breaks: [],
  },
  diamante: {
    id: "diamante",
    lines: [
      line({ words: 1, hint: "diamante1" }),
      line({ words: 2, hint: "diamante2" }),
      line({ words: 3, hint: "diamante3" }),
      line({ words: 4, hint: "diamante4" }),
      line({ words: 3, hint: "diamante5" }),
      line({ words: 2, hint: "diamante6" }),
      line({ words: 1, hint: "diamante7" }),
    ],
    breaks: [],
  },
  limerick: {
    id: "limerick",
    lines: [
      line({ syllables: [8, 9], rhyme: "A" }),
      line({ syllables: [8, 9], rhyme: "A" }),
      line({ syllables: [5, 6], rhyme: "B" }),
      line({ syllables: [5, 6], rhyme: "B" }),
      line({ syllables: [8, 9], rhyme: "A" }),
    ],
    breaks: [],
  },
  shakespearean: sonnet("shakespearean", "ABABCDCDEFEFGG", [3, 7, 11], {
    8: "volta",
    12: "couplet",
  }),
  petrarchan: sonnet("petrarchan", "ABBAABBACDCDCD", [7], { 8: "volta" }),
  villanelle: villanelle(),
};

/* ---------------------------------------------------------------- guidance */

export type SyllableStatus =
  | "empty"
  | "match"
  | "over"
  | "under"
  | "possible"
  | "none";

export type RhymeStatus = "first" | "match" | "slant" | "mismatch" | "pending";

export interface LineGuide {
  index: number;
  /** The text checked: the typed line, or the refrain it copies. */
  text: string;
  words: number;
  syllables: number | null;
  /** How far the syllable total is from the target range (negative: short). */
  difference: number;
  syllableStatus: SyllableStatus;
  wordStatus: "empty" | "match" | "over" | "under" | "none";
  rhyme: {
    letter: string;
    status: RhymeStatus;
    /** The earlier line it should rhyme with. */
    with: number | null;
    kind: SlantKind | null;
    guess: boolean;
  } | null;
}

function toLineInfo(text: string, number: number, lookup: Lookup): LineInfo {
  return { text, number, words: lineWords(text, lookup), hasDigits: false };
}

function syllableGuide(
  spec: FormLine,
  info: LineInfo
): Pick<LineGuide, "syllables" | "difference" | "syllableStatus"> {
  if (!spec.syllables) {
    return { syllables: null, difference: 0, syllableStatus: "none" };
  }
  if (info.words.length === 0) {
    return { syllables: 0, difference: 0, syllableStatus: "empty" };
  }
  const [min, max] = spec.syllables;
  const count = lineSyllables(info.words, {});
  if (count >= min && count <= max) {
    return { syllables: count, difference: 0, syllableStatus: "match" };
  }
  const reachable = [...reachableTotals(info.words)];
  const difference = count < min ? count - min : count - max;
  const possible = reachable.some((total) => total >= min && total <= max);
  let syllableStatus: SyllableStatus = difference > 0 ? "over" : "under";
  if (possible) {
    syllableStatus = "possible";
  }
  return { syllables: count, difference, syllableStatus };
}

function wordGuide(spec: FormLine, info: LineInfo): LineGuide["wordStatus"] {
  if (spec.words === null) {
    return "none";
  }
  if (info.words.length === 0) {
    return "empty";
  }
  if (info.words.length === spec.words) {
    return "match";
  }
  return info.words.length > spec.words ? "over" : "under";
}

function rhymeGuide(
  spec: FormSpec,
  index: number,
  infos: readonly LineInfo[]
): LineGuide["rhyme"] {
  const letter = spec.lines[index].rhyme;
  if (!letter) {
    return null;
  }
  const mine = infos[index];
  if (mine.words.length === 0) {
    return { letter, status: "pending", with: null, kind: null, guess: false };
  }
  const earlier = spec.lines.findIndex(
    (candidate, i) =>
      i < index && candidate.rhyme === letter && infos[i].words.length > 0
  );
  if (earlier < 0) {
    return { letter, status: "first", with: null, kind: null, guess: false };
  }
  const a = endWord(infos[earlier]);
  const b = endWord(mine);
  if (!(a && b)) {
    return {
      letter,
      status: "pending",
      with: earlier,
      kind: null,
      guess: false,
    };
  }
  const link = relate(a, b);
  let status: RhymeStatus = "mismatch";
  if (link.relation === "perfect" || link.relation === "identical") {
    status = "match";
  } else if (link.relation === "slant") {
    status = "slant";
  }
  return { letter, status, with: earlier, kind: link.kind, guess: link.guess };
}

/** Check a draft against a form, one guide per line. `texts` are the typed lines. */
export function evaluateForm(
  spec: FormSpec,
  texts: readonly string[],
  lookup: Lookup
): LineGuide[] {
  const shown = spec.lines.map((entry, i) =>
    entry.refrain === null ? (texts[i] ?? "") : (texts[entry.refrain] ?? "")
  );
  const infos = shown.map((text, i) => toLineInfo(text, i + 1, lookup));
  return spec.lines.map((entry, index) => ({
    index,
    text: shown[index],
    words: infos[index].words.length,
    ...syllableGuide(entry, infos[index]),
    wordStatus: wordGuide(entry, infos[index]),
    rhyme: rhymeGuide(spec, index, infos),
  }));
}

/** The draft as plain text, with refrains filled in and stanza breaks kept. */
export function formText(spec: FormSpec, texts: readonly string[]): string {
  const out: string[] = [];
  for (const [i, entry] of spec.lines.entries()) {
    const source = entry.refrain === null ? i : entry.refrain;
    out.push((texts[source] ?? "").trim());
    if (spec.breaks.includes(i)) {
      out.push("");
    }
  }
  return out.join("\n").trimEnd();
}

/** True when every line meets its syllable or word target and every rhyme holds. */
export function isComplete(guides: readonly LineGuide[]): boolean {
  return guides.every((guide) => {
    const counts =
      guide.syllableStatus === "match" || guide.syllableStatus === "none";
    const words = guide.wordStatus === "match" || guide.wordStatus === "none";
    const rhyme =
      !guide.rhyme ||
      guide.rhyme.status === "match" ||
      guide.rhyme.status === "first";
    return counts && words && rhyme && guide.text.trim().length > 0;
  });
}

const RE_BLANK_LINE = /\n{2,}/;

/** A standalone page for printing a poem: title, then stanzas of lines. */
export function printPoemPage(title: string, text: string): string {
  const stanzas = text
    .split(RE_BLANK_LINE)
    .map(
      (stanza) => `<p>${stanza.split("\n").map(escapeHtml).join("<br>")}</p>`
    )
    .join("");
  return `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title><style>@page{margin:2cm}body{font-family:Georgia,'Times New Roman',serif;color:#111;margin:0}h1{font-size:22pt;text-align:center;margin:0 0 1.2em;font-weight:normal}p{font-size:14pt;line-height:1.7;margin:0 0 1.2em}</style></head><body><h1>${escapeHtml(title)}</h1>${stanzas}</body></html>`;
}
